import { Link } from '@mui/material';

import {
  Column,
  EllipsisWithTooltip,
  NoData,
  TableHeader,
  Typography,
  Box,
  Stack,
  tokens,
} from '@cast/design-system';
import type { BillingV1FeatureEntityUsage } from '@cast/types';

import { Link as RouterLink } from 'components/router';
import { tableCacheKeys } from 'constants/tableCacheKeys';
import { buildEnterpriseUrl } from '@cast/infrastructure-organization';

import { FailedToLoad } from '@cast/shared-messages-ui';

import {
  NoResults,
  Search,
  SearchableTable,
  SearchableTableRecordsCount,
  SearchProvider,
} from '@cast/shared-search-ui';

type EnterpriseEntitiesTableProps = {
  entities: BillingV1FeatureEntityUsage[];
  entityColorMap?: Map<string, string>;
  unit: string;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
  selectedRows: string[];
  onRowsSelected: (ids: string[]) => void;
  maxSelection: number;
  enterpriseId?: string;
  feature?: string;
};

export const EnterpriseEntitiesTable = ({
  entities,
  entityColorMap,
  unit,
  isLoading,
  isError,
  refetch,
  selectedRows,
  onRowsSelected,
  maxSelection,
  enterpriseId,
  feature,
}: EnterpriseEntitiesTableProps) => {
  const sorted = [...entities].sort((a, b) => (b.usage ?? 0) - (a.usage ?? 0));

  const handleRowsSelected = (ids: string[]) => {
    const retained = selectedRows.filter((id) => ids.includes(id));
    const added = ids.filter((id) => !selectedRows.includes(id));
    const merged = [...retained, ...added].slice(0, maxSelection);
    onRowsSelected(merged);
  };

  return (
    <SearchProvider<BillingV1FeatureEntityUsage>
      urlKey="enterprise-platform-usage-entities"
      data={sorted}
      isLoading={isLoading}
      freeTextPredicate={(item, text) =>
        !!(
          item.entityName?.toLowerCase().includes(text.toLowerCase()) ||
          item.entityId?.toLowerCase().includes(text.toLowerCase())
        )
      }
    >
      <Stack gap={8}>
        <Stack direction="row" alignItems="center" gap={8}>
          <Search />
        </Stack>
        <SearchableTable<BillingV1FeatureEntityUsage>
          cacheKey={tableCacheKeys.PLATFORM_USAGE.ENTITIES}
          testId="enterprise-platform-usage-entities-table"
          rowKey={(item) => item.entityId ?? item.entityName ?? ''}
          fillParentHeight
          hasFailed={isError}
          selectedRows={selectedRows}
          onRowsSelected={(ids) => handleRowsSelected(ids as string[])}
          components={{
            noData: <NoData entity="organization" />,
            noResults: <NoResults entity="organization" />,
            failed: <FailedToLoad refresh={refetch} />,
          }}
          outerHeader={
            <TableHeader
              recordsCount={
                <SearchableTableRecordsCount title="organization" />
              }
            />
          }
        >
          <Column<BillingV1FeatureEntityUsage>
            id="name"
            header="Organization"
            minWidth={320}
            sortable
            accessor="entityName"
            renderCell={(item) => {
              const key = item.entityId ?? item.entityName ?? '';
              const color = entityColorMap?.get(key);
              const orgLink =
                enterpriseId && feature && item.entityId
                  ? buildEnterpriseUrl(
                      enterpriseId,
                      `billing-report/${feature}`,
                      item.entityId
                    )
                  : undefined;
              return (
                <Stack direction="row" alignItems="center" gap={8}>
                  {color ? (
                    <Box
                      sx={{
                        width: 2,
                        height: 32,
                        borderRadius: '2px',
                        bgcolor: color,
                        flexShrink: 0,
                      }}
                    />
                  ) : (
                    <Box sx={{ width: 2, flexShrink: 0 }} />
                  )}
                  <EllipsisWithTooltip TypographyProps={{ variant: 'P12M' }}>
                    {orgLink ? (
                      <Link
                        component={RouterLink}
                        to={orgLink}
                        underline="hover"
                        sx={{ color: 'inherit', typography: 'P12M' }}
                      >
                        {item.entityName ?? '–'}
                      </Link>
                    ) : (
                      item.entityName ?? '–'
                    )}
                  </EllipsisWithTooltip>
                </Stack>
              );
            }}
          />
          <Column<BillingV1FeatureEntityUsage>
            id="usage"
            header="Total usage"
            infoTooltip={`Average monthly billable ${unit} for this organization. Provisioned usage is normalized, meaning a ${unit} running 15 days in a 30-day month counts as 0.5 billable ${unit}.`}
            width={200}
            sortable
            accessor="usage"
            renderCell={(item) => (
              <Stack direction="row" alignItems="baseline" gap={4}>
                <Typography
                  color={tokens.theme.fg.structural.primary}
                  variant="P12B"
                >
                  {(item.usage ?? 0).toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </Typography>
                <Typography
                  variant="P12R"
                  color={tokens.theme.fg.structural.secondary}
                >
                  {unit}
                </Typography>
              </Stack>
            )}
          />
        </SearchableTable>
      </Stack>
    </SearchProvider>
  );
};
